// angular
import {
  afterNextRender,
  inject,
  Injectable,
  PLATFORM_ID,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isPlatformBrowser } from '@angular/common';
// services
import { FirebaseService } from './firebase.service';
import { StateManagementService } from '@app/+chat/shared/services/state-management.service';
import { CurrencyService } from '@app/core/shared/services/currency.service';
import { UserService } from './user.service';
import { StorageService } from './storage.service';
import { CanonicalService } from './canonical.service';
import { NoIndexService } from './no-index.service';
// env & interfaces & enums
import { environment } from '@environments/environment';
import { CurrencyCodes } from '../enums/currencies.enum';
import { I18n } from '../enums/i18n.enum';
// firebase
import { initializeApp } from 'firebase/app';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { SeoTagsService } from './seo-tags.service';

@Injectable()
export class InitService {
  private _renderer!: Renderer2;
  private _token!: string;
  // dependencies
  private readonly _userService = inject(UserService);
  private readonly _translateService = inject(TranslateService);
  private readonly _storageService = inject(StorageService);
  private readonly _stateManagementService = inject(StateManagementService);
  private readonly _rendererFactory = inject(RendererFactory2);
  private readonly _canonicalService = inject(CanonicalService);
  private readonly _firebaseService = inject(FirebaseService);
  private readonly _currencyService = inject(CurrencyService);
  private readonly _noIndexService = inject(NoIndexService);
  private readonly _gtmService = inject(GoogleTagManagerService);
  private readonly _seoTagsService = inject(SeoTagsService);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {
    // Get an instance of Renderer2
    this._renderer = this._rendererFactory.createRenderer(null, null);

    afterNextRender(() => {
      // call firebase initializer function
      const app = initializeApp(environment.firebase);
      this._firebaseService.requestPermission(app);
      this._firebaseService.listen(app);
    });
  }

  public async init() {
    // set canonical url
    if (environment.production) {
      this._canonicalService.setCanonicalURL();
    } else {
      this._noIndexService.setNoindexMetaTag();
    }

    this._translateService.onLangChange.subscribe(val => {
      // set website lang
      this._storageService.setLang(val.lang);
      // set chat lang
      if (!this._storageService.getChatLang()) {
        this._stateManagementService.currentChatLanguage.set(val.lang);
        this._storageService.setChatLang(val.lang);
      }
      // set direction
      const dir =
        val.lang === 'fa' || val.lang === 'ar' || val.lang === 'ckb'
          ? 'rtl'
          : 'ltr';

      // set body direction
      if (typeof document !== 'undefined') {
        this._renderer.setAttribute(document.body, 'dir', dir);
      }

      if (val.lang === I18n.fa) {
        this._currencyService.currentCurrency.set(CurrencyCodes.irr);
        this._storageService.setCurrency(CurrencyCodes.irr);
      } else {
        this._currencyService.currentCurrency.set(CurrencyCodes.usd);
        this._storageService.setCurrency(CurrencyCodes.usd);
      }
    });

    // set language in translate service
    if (this._storageService.getLang()) {
      this._translateService.use(this._storageService.getLang());
    } else {
      this._translateService.use(environment.defaultLang);
    }

    // check if token is exist then get user info
    this._userService.getToken().then(token => {
      if (token) {
        this._token = token;

        this._userService.getUserInfo().subscribe({
          next: data => {
            // set chat language from profile
            if (data.language) {
              this._stateManagementService.currentChatLanguage.set(
                data.language
              );
              this._storageService.setChatLang(data.language);
            }

            // check if user account type is business then navigate to portfolio page

            // if (environment.production) {
            //   if (data.type === 'BUSINESS' && data.business?.is_active) {
            //     this._router.navigate([`/${data.business?.custom_url}`]);
            //   } else if (
            //     data.type === 'BUSINESS' &&
            //     !data.business?.is_active
            //   ) {
            //     window.location.href = `${environment.baseUrl.panelUrl}/user`;
            //   } else {
            //     this._router.navigate(['coming-soon']);
            //   }
            // }
          },
          error: errInfo => {
            if (errInfo?.error?.statusCode === 401) {
              if (window) {
                this._userService.logout();
              }
            }
          },
        });
      } else {
        this._stateManagementService.currentChatLanguage.set(
          this._storageService.getChatLang() ||
            this._translateService.currentLang
        );
      }
      // else {
      //   // check if user do not login yet then navigate to panel login page
      //   if (environment.production) {
      //     window.location.href = environment.baseUrl.panelUrl;
      //   }
      // }
    });

    // set yektanet tag
    if (environment.production) {
      this._seoTagsService.addYektaNetScript();
    }
    // set matomo script
    // this._seoTagsService.addMatomoScript();

    if (isPlatformBrowser(this._platformId)) {
      // connect gtm service
      this._gtmService.addGtmToDom().then(_ => {});
    }
  }
}
